import type { PropsWithChildren, ReactNode } from 'react';
import { useCallback, useEffect } from 'react';

import { QueryErrorResetBoundary, useQueryErrorResetBoundary } from '@tanstack/react-query';
import type { ErrorComponentProps } from '@tanstack/react-router';
import { useRouter } from '@tanstack/react-router';
import type { FallbackProps } from 'react-error-boundary';
import { ErrorBoundary } from 'react-error-boundary';

import { reportError, reportErrorBoundaryError } from '@/_shared/utils/sentry-utils';

import { Button } from '../button';

export function ErrorBoundaryWithRetry({
  children,
  fallbackRender,
}: PropsWithChildren<{ fallbackRender?: (props: FallbackProps) => ReactNode }>) {
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundary
          fallbackRender={fallbackRender || OnError}
          onError={(error, errorInfo) => {
            // Report to Sentry
            reportErrorBoundaryError(error as Error, errorInfo);
          }}
          onReset={reset}
        >
          {children}
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
}

export function OnError({ resetErrorBoundary }: FallbackProps) {
  return (
    <section className="flex w-full flex-col items-center justify-center gap-4 overflow-hidden py-4">
      <div className="text-c-card-muted">There was an error!</div>
      <Button onClick={() => resetErrorBoundary()}>Try again</Button>
    </section>
  );
}

export const PageError = ({ reset, error }: ErrorComponentProps) => {
  const router = useRouter();
  const queryErrorResetBoundary = useQueryErrorResetBoundary();

  useEffect(() => {
    queryErrorResetBoundary.reset();

    // Report page errors to Sentry
    reportError(error);
  }, [queryErrorResetBoundary, error]);

  const { message } = error;

  const render = useCallback(() => {
    if (message.indexOf('dynamically imported module') > -1) {
      return (
        <>
          <div className="text-c-card-muted mt-32 mb-4 text-center text-lg font-bold">
            Something went wrong while loading the page.
            <br />
            <div className="text-sm font-normal">Please check your internet connection and try again.</div>
          </div>
          <Button
            onClick={() => {
              reset();
              router.invalidate();
              window.location.reload();
            }}
          >
            Try again
          </Button>
        </>
      );
    }

    if (message === 'Something went wrong while loading data.') {
      return (
        <>
          <div className="text-c-card-muted mt-8 mb-4 text-center text-lg font-bold">
            {/* {message}
            <br />
            <br /> */}
            <div className="text-sm font-normal">Please check your internet connection and try again.</div>
          </div>
          <Button
            onClick={() => {
              reset();
              router.invalidate();
            }}
          >
            Try again
          </Button>
        </>
      );
    }

    return (
      <>
        <div className="text-c-card-muted font-lg mt-8 mb-4 text-center text-base">
          {/* {message}
          <br />
          <br /> */}
          <div className="text-sm font-normal">Please check your internet connection and try again.</div>
          <div className="text-sm font-normal">If the Issue persists please contact Admin</div>
        </div>
        <Button
          onClick={() => {
            reset();
            router.invalidate();
            window.location.reload();
          }}
        >
          Try again
        </Button>
      </>
    );
  }, [message, reset, router]);

  console.log(error);

  return <section className="flex w-full flex-col items-center justify-center gap-6">{render()}</section>;
};
